// @flow

import type { AttachmentTypes } from "../../models/Attachment/Attachment";
import { AttachmentTypeEnum } from "../../models/Attachment/Attachment";
import AttachmentStorageApi from "../../api/AttachmentApi/AttachmentStorageApi";
import PaymentApi from "../../api/PaymentApi/PaymentApi";
import type { CreatePaymentRequest, UpdatePaymentRequest, Payment } from "../../models/Payment/Payment";
import { AppToaster } from "../../components/Toaster/Toaster";
import { Intent } from "@blueprintjs/core";

class PaymentService {

  paymentApi: PaymentApi;
  attachmentStorageApi: AttachmentStorageApi;

  constructor(paymentApi: PaymentApi, attachmentStorageApi: AttachmentStorageApi) {
    this.paymentApi = paymentApi;
    this.attachmentStorageApi = attachmentStorageApi;
  }

  async getPaymentsForProject(projectId: string): Promise<Array<Payment>> {
    return await this.paymentApi.getPaymentsForProject(projectId);
  }

  async createPayment(createPaymentRequest: CreatePaymentRequest): Promise<Payment> {
    const payment = await this.paymentApi.createPayment(createPaymentRequest);

    if (payment.attachment) {
      try {
        await this.attachmentStorageApi.putAttachmentPayload(
          payment.attachment.putUrl,
          createPaymentRequest,
          AttachmentTypeEnum.payment
        );
        AppToaster.show({
          message: "Payment created with receipt",
          intent: Intent.SUCCESS,
        });
      } catch (error) {
        AppToaster.show({
          message: `Payment created but receipt upload failed: ${error.message || 'Unknown error'}`,
          intent: Intent.DANGER,
        });
        throw new Error(`Payment created but attachment upload failed: ${error.message || 'Unknown error'}`);
      }
    } else {
      AppToaster.show({
        message: "Payment created",
        intent: Intent.SUCCESS,
      });
    }

    return payment;
  }

  async deletePayment(projectId: string, paymentId: string): Promise<void> {
    await this.paymentApi.deletePayment(projectId, paymentId);
    AppToaster.show({
      message: "Payment deleted",
      intent: Intent.SUCCESS,
    });
  }

  async updatePayment(updatePaymentRequest: UpdatePaymentRequest): Promise<Payment> {
    const payment = await this.paymentApi.updatePayment(updatePaymentRequest);

    if (payment && payment.attachment && payment.attachment.putUrl) {
      try {
        await this.attachmentStorageApi.putAttachmentPayload(
          payment.attachment.putUrl,
          updatePaymentRequest,
          AttachmentTypeEnum.payment
        );
        AppToaster.show({
          message: "Payment updated with receipt",
          intent: Intent.SUCCESS,
        });
      } catch (error) {
        AppToaster.show({
          message: `Payment updated but receipt upload failed: ${error.message || 'Unknown error'}`,
          intent: Intent.DANGER,
        });
        throw new Error(`Payment updated but attachment upload failed: ${error.message || 'Unknown error'}`);
      }
    } else {
      AppToaster.show({
        message: "Payment updated",
        intent: Intent.SUCCESS,
      });
    }

    return payment;
  }
}

export default PaymentService;
